
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import { Trash2, MessageCircle } from "lucide-react";
import { Comment, getArticleComments, addComment, deleteComment } from "@/services/commentService";
import { formatDistanceToNow } from "date-fns";
import { toolForm, toolParam } from "@/lib/webmcp/attrs";
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

interface CommentSectionProps {
  articleId: string;
}

const CommentSection = ({ articleId }: CommentSectionProps) => {
  const t = useT();
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [articleId]);

  const fetchComments = async () => {
    try {
      setIsLoading(true);
      const data = await getArticleComments(articleId);
      setComments(data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!user || !newComment.trim()) return;

    try {
      setIsSubmitting(true);
      const comment = await addComment(articleId, user.id, newComment.trim());
      setComments(prev => [...prev, comment]);
      setNewComment("");
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment(commentId);
      setComments(prev => prev.filter(comment => comment.id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <MessageCircle className="h-5 w-5" />
        <h3 className="text-lg font-semibold">
          Comments ({comments.length})
        </h3>
      </div>

      {isAuthenticated && (
        <form
          className="space-y-3"
          {...toolForm("postStoryComment", "Post a comment on the Byteline story the reader is viewing.")}
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmitComment();
          }}
        >
          <label htmlFor="story-comment-text" className="sr-only">
            <T k="auto.your-comment-on-this-story-514b" />
          </label>
          <Textarea
            id="story-comment-text"
            name="comment"
            placeholder={t("auto.share-your-thoughts-94d1")}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="min-h-[100px]"
            {...toolParam("The comment text to post on this story.")}
          />
          <Button
            type="submit"
            disabled={isSubmitting || !newComment.trim()}
          >
            {isSubmitting ? "Posting..." : "Post Comment"}
          </Button>
        </form>
      )}

      {!isAuthenticated && (
        <div className="text-center py-8 text-muted-foreground">
          <p><T k="auto.please-sign-in-to-join-the-discussion-5b52" /></p>
        </div>
      )}

      <div className="space-y-4">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="h-10 w-10 bg-muted rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/4" />
                  <div className="h-16 bg-muted rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : comments.length > 0 ? (
          comments.map((comment) => (
            <div key={comment.id} className="flex gap-3 p-4 border rounded-lg">
              <ProfileAvatar
                userId={comment.user_id}
                avatarUrl={comment.profiles?.avatar_url}
                name={comment.profiles?.full_name}
                background={comment.profiles?.avatar_background}
                size="sm"
              />
              <div className="flex-1 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{comment.profiles?.full_name || 'Anonymous'}</span>
                    <span className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  {user?.id === comment.user_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <p className="text-sm leading-relaxed">{comment.content}</p>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p><T k="auto.no-comments-yet-be-the-first-to-share-yo-849d" /></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentSection;
